import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Component to help users recover from authentication failures
 * Provides options to retry login or use emergency auth methods
 */
const RecoverAuth: React.FC = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const [hasPreviousSession, setHasPreviousSession] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [apiUrl] = useState<string>(import.meta.env.VITE_API_BASE_URL || '/api/v1');
  
  // Check if there's user info in localStorage from previous session
  useEffect(() => {
    try {
      // Check for user email from previous session
      const emailData = localStorage.getItem('kms_user_email');
      if (emailData) {
        try {
          // Remove quotes if present
          let email = emailData;
          if (email.startsWith('"') && email.endsWith('"')) {
            email = email.substring(1, email.length - 1);
          }
          setUserEmail(email);
          // If we have user info, we can attempt session recovery
          setHasPreviousSession(true);
        } catch (e) {
          console.error('Error parsing user email:', e);
        }
      }
    } catch (e) {
      console.error('Error checking localStorage:', e);
    }
  }, []);
  
  // Set up countdown to auto-redirect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/login');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [navigate]);
  
  // Function to attempt direct auth recovery
  const attemptDirectAuth = async () => {
    try {
      // Show recovery in progress
      setCountdown(-1); // Disable auto-redirect
      
      // Attempt to check session-based authentication
      try {
        console.log('Attempting session recovery authentication...');
        const response = await fetch(`${apiUrl}/auth/status`, {
          method: 'GET',
          credentials: 'include', // This is the key for session-based auth
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated) {
            // On success, redirect to home
            navigate('/?recovery=success');
            return;
          }
        }
        
        // If session recovery failed, redirect to login
        alert('Session recovery failed. Please log in again.');
        navigate('/login');
      } catch (err) {
        alert(`Error during authentication recovery: ${err instanceof Error ? err.message : String(err)}`);
        navigate('/login');
      }
    } catch (err) {
      alert(`Error during authentication recovery: ${err instanceof Error ? err.message : String(err)}`);
      navigate('/login');
    }
  };
  
  // Go to the debug page
  const goToDebugPage = () => {
            navigate('/documents');
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Failed</h2>
          <p className="text-gray-600 mb-6">
            Could not verify your authentication after multiple attempts. Please try logging in again.
          </p>
          
          {hasPreviousSession && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>Previous session detected.</strong> You have user information that might help recover your session.
              </p>
              {userEmail && (
                <p className="text-sm text-yellow-800 mb-2">
                  Email: <strong>{userEmail}</strong>
                </p>
              )}
              <button
                onClick={attemptDirectAuth}
                className="w-full mt-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              >
                Try Emergency Recovery
              </button>
            </div>
          )}
          
          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Return to Login
            </button>
            
            <button
              onClick={goToDebugPage}
              className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Open Debug Tools
            </button>
          </div>
          
          {countdown > 0 && (
            <p className="text-sm text-gray-500 mt-4">
              This window will redirect to login in {countdown} seconds...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoverAuth; 